import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Story', to: '/story' },
  { label: 'Investing', to: '/investing' },
  { label: 'Building', to: '/building' },
  { label: 'Advisory', to: '/advisory' },
];

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const showSolid = !transparent || scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-8 lg:px-12 transition-all duration-500 ${
        showSolid ? 'pt-3' : 'pt-6'
      }`}
    >
      <div
        className={`mx-auto max-w-7xl liquid-glass rounded-2xl flex items-center justify-between px-4 py-2 transition-all duration-500 ${
          showSolid ? 'bg-black/70' : ''
        }`}
      >
        <Link
          to="/"
          className="flex items-center gap-2 group"
        >
          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-white text-black font-bold tracking-tight">
            V
          </span>
          <span className="text-2xl font-semibold tracking-tight font-display">VEX</span>
        </Link>

        <div className="hidden md:flex gap-1 text-sm">
          {navLinks.map((link) => {
            const active = location.pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`relative px-4 py-2 rounded-lg transition-colors hover-shine ${
                  active ? 'text-white' : 'text-white/70 hover:text-white'
                }`}
              >
                {link.label}
                {active && (
                  <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
                )}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/advisory"
            className="hidden sm:inline-flex bg-white text-black px-5 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 whitespace-nowrap cursor-pointer transition-colors"
          >
            Start a Chat
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg border border-white/15 text-white cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-lg`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden mx-auto max-w-7xl overflow-hidden transition-all duration-500 ${
          mobileOpen ? 'max-h-96 mt-3' : 'max-h-0'
        }`}
      >
        <div className="liquid-glass rounded-2xl p-4 flex flex-col gap-1 bg-black/80">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="px-3 py-2 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors"
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/advisory"
            className="mt-2 bg-white text-black px-4 py-2 rounded-lg text-sm font-medium text-center"
          >
            Start a Chat
          </Link>
        </div>
      </div>
    </nav>
  );
}